import React, { useEffect, useState } from 'react';
import { Container } from 'reactstrap';
import { useIntl } from 'react-intl';
import { useLocation, Redirect, useParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';
import { useDispatch, shallowEqual } from 'react-redux';
import { Button, ImageCardButton } from '@whitelabel/component-library';
import commonMessage from '@whitelabel/helpers/messages/commonMsg';
import { ReactStateSetter } from '@whitelabel/helpers/types';
import { HELP_LOGIN_MODAL_DISPLAYED, SKYSCANNER_PARTNER_ID } from '@whitelabel/xcover-shared/helpers/constants';
import { IHelpPagePartner } from '@whitelabel/xcover-shared/helpers/types';
import LoginModal from '../LoginModal';
import { getPartners } from '../../actions';
import { HelpCenterPageType } from '../../helpers/types';
import { useGetHelpArticleListsContentQuery } from '../../store/services/xcover/endpoints/help';
import { useAppSelector } from '../../store/hooks';
import {
  StyledBGContainer,
  StyledTitle,
  StyledBottomBox,
  StyledLoginBlock,
  StyledPartnersBlock,
  StyledPartnersBox,
  StyledWelcomeText,
  StyledAlert,
} from './styledHelpCenterHero';
import messages from './messages';

interface IHelpCenterHeroProps {
  hideLoginOnDesktop?: boolean;
  hidePartnersBlock?: boolean;
  insPartners?: IHelpPagePartner[];
  loadingINSPartners?: boolean;
  isLoginModalOpen: boolean;
  setIsLoginModalOpen: ReactStateSetter<boolean>;
}

const HelpCenterHero = ({
  hideLoginOnDesktop,
  hidePartnersBlock,
  insPartners,
  loadingINSPartners,
  isLoginModalOpen,
  setIsLoginModalOpen,
}: IHelpCenterHeroProps): JSX.Element => {
  const theme = useTheme();
  const { formatMessage, locale, bidi } = useIntl();
  const { pathname } = useLocation();
  const [dummyBoxes, setDummyBoxes] = useState<number[]>();
  const isTablet = useMediaQuery({ minWidth: theme.gridBreakpoints.md });
  const isDesktop = useMediaQuery({ minWidth: theme.gridBreakpoints.lg });
  const { type: helpType, slug: helpSlug }: { type: HelpCenterPageType; slug: string } = useParams();
  const dispatch = useDispatch();
  const { user, customer, partners, error } = useAppSelector(
    ({
      user: { data: userData },
      customer: { data: customerData },
      partners: { data: partnersData, error: partnersError },
    }) => ({
      user: userData,
      customer: customerData,
      partners: partnersData[locale],
      error: partnersError,
    }),
    shallowEqual,
  );
  const toggleModalOpen = () => {
    setIsLoginModalOpen((prev) => !prev);
  };

  const { data: article } = useGetHelpArticleListsContentQuery(
    { helpType, slug: helpSlug, locale, auth: !!user },
    { skip: !helpType || !helpSlug },
  );
  const isPartnerPhoneBased = article?.partner?.signupMethodOnXcover === 'phone' || false;

  const localPartners: IHelpPagePartner[] = partners || insPartners;
  const hideBottomBoxOnDesktop = hideLoginOnDesktop && !user && !localPartners;

  useEffect(() => {
    function updateDummyBoxes(totalBoxesInARow: number) {
      const reminder = localPartners.length % totalBoxesInARow;
      if (reminder) {
        setDummyBoxes(Array(totalBoxesInARow - reminder).fill(1));
      } else {
        setDummyBoxes(undefined);
      }
    }
    if (localPartners) {
      if (isDesktop) {
        updateDummyBoxes(4);
      } else if (isTablet) {
        updateDummyBoxes(3);
      } else {
        setDummyBoxes(undefined);
      }
    }
  }, [isTablet, isDesktop, localPartners]);

  useEffect(() => {
    if (user && customer && !hidePartnersBlock && !partners) {
      dispatch(getPartners(customer.id));
    }
  }, [user, getPartners, customer, partners, hidePartnersBlock]);

  const clickLoginButton = () => {
    setIsLoginModalOpen(true);
  };

  const renderLoginBlock = () => (
    <StyledLoginBlock>
      <div>
        <h2 className="h4">{formatMessage(messages.loginQuestion)}</h2>
        {formatMessage(messages.loginText)}
      </div>
      <Button buttonType="secondary" onClick={clickLoginButton}>
        {formatMessage(messages.loginButtonText)}
      </Button>
    </StyledLoginBlock>
  );

  function renderPartnersBlock() {
    return (
      <StyledPartnersBlock>
        <Container>
          <StyledWelcomeText $hasPartners={!!localPartners}>{formatMessage(messages.welcomeText)}</StyledWelcomeText>
          {error && <StyledAlert color="danger">{formatMessage(commonMessage.error)}</StyledAlert>}
          {localPartners && (
            <StyledPartnersBox>
              {localPartners.map(({ id, logo: logoRaw, title, slug }) => {
                let logo: undefined | string = logoRaw;
                if (id === SKYSCANNER_PARTNER_ID) {
                  // business requirement: don't show skyscanner logo
                  logo = undefined;
                }
                return (
                  <ImageCardButton
                    key={id}
                    image={logo}
                    title={formatMessage(messages.protectionText, { partnerName: title })}
                    link={`/help/partners/${slug}`}
                    alt={formatMessage(commonMessage.logo, { brand: title })}
                  />
                );
              })}
              {dummyBoxes && dummyBoxes.map((element, index) => <div key={element + index} />)}
            </StyledPartnersBox>
          )}
        </Container>
      </StyledPartnersBlock>
    );
  }

  if (pathname === `/${locale}/help` && localPartners?.length === 1) {
    return <Redirect to={`/${locale}/help/partners/${localPartners[0].slug}`} />;
  }

  return (
    <>
      <StyledBGContainer $rtl={bidi} $hideBottomBoxOnDesktop={hideBottomBoxOnDesktop}>
        <Container>
          <StyledTitle>
            <span>{formatMessage(messages.title)}</span>
          </StyledTitle>
        </Container>
      </StyledBGContainer>
      {isLoginModalOpen && (
        <LoginModal
          toggleModalOpen={toggleModalOpen}
          isOpen={isLoginModalOpen}
          loginModalDisplayPlace={HELP_LOGIN_MODAL_DISPLAYED}
          isPartnerPhoneBased={isPartnerPhoneBased}
        />
      )}
      {!loadingINSPartners && (
        <>
          {((user && customer) || localPartners) && !hidePartnersBlock && renderPartnersBlock()}
          <StyledBottomBox $hideOnDesktop={hideBottomBoxOnDesktop}>{!user && renderLoginBlock()}</StyledBottomBox>
        </>
      )}
    </>
  );
};

export default HelpCenterHero;
